import { IMapService, MapProvider, MapConfig } from './IMapService'
import { GoogleMapsService } from './GoogleMapsService'
import { OpenStreetMapService } from './OpenStreetMapService'

export class MapServiceFactory {
  private static instance: IMapService | null = null
  private static currentProvider: MapProvider | null = null

  static getMapService(config?: MapConfig): IMapService {
    // Déterminer le provider à utiliser
    const provider = config?.provider || this.getProviderFromEnv()
    
    // Si le provider a changé, créer une nouvelle instance
    if (this.currentProvider !== provider || !this.instance) {
      this.cleanup() // Nettoyer l'ancienne instance
      
      switch (provider) {
        case MapProvider.GOOGLE:
          console.log('🗺️ Using Google Maps provider')
          this.instance = new GoogleMapsService()
          break
        case MapProvider.OPENSTREETMAP:
          console.log('🗺️ Using OpenStreetMap provider')
          this.instance = new OpenStreetMapService()
          break
        default:
          console.warn(`Unknown map provider: ${provider}, falling back to OpenStreetMap`)
          this.instance = new OpenStreetMapService()
      }
      
      this.currentProvider = provider
    }
    
    return this.instance
  }

  private static getProviderFromEnv(): MapProvider {
    const envProvider = process.env.REACT_APP_MAP_PROVIDER?.toLowerCase()
    
    switch (envProvider) {
      case 'google':
        return MapProvider.GOOGLE
      case 'openstreetmap':
      case 'osm':
        return MapProvider.OPENSTREETMAP
      default:
        // Par défaut, utiliser OpenStreetMap (gratuit)
        console.log('🗺️ No map provider specified, defaulting to OpenStreetMap')
        return MapProvider.OPENSTREETMAP
    }
  }

  static getCurrentProvider(): MapProvider | null {
    return this.currentProvider
  }

  static switchProvider(provider: MapProvider): IMapService {
    console.log(`🔄 Switching map provider to: ${provider}`)
    this.cleanup()
    this.currentProvider = null
    return this.getMapService({ provider, defaultLocation: { latitude: 40.7128, longitude: -74.0060 } })
  }

  static cleanup(): void {
    if (this.instance) {
      try {
        this.instance.cleanup()
      } catch (error) {
        console.warn('Error during map service cleanup:', error)
      }
      this.instance = null
    }
  }

  // Méthode utilitaire pour vérifier si Google Maps est disponible
  static isGoogleMapsAvailable(): boolean {
    const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY
    return !!(apiKey && apiKey !== 'undefined' && apiKey.trim() !== '')
  }

  // Méthode pour obtenir le provider recommandé
  static getRecommendedProvider(): MapProvider {
    // Si Google Maps est configuré et disponible, le recommander
    if (this.isGoogleMapsAvailable()) {
      return MapProvider.GOOGLE
    }
    
    // Sinon, recommander OpenStreetMap (gratuit)
    return MapProvider.OPENSTREETMAP
  }

  // Méthode pour obtenir des informations sur les providers disponibles
  static getAvailableProviders(): Array<{
    provider: MapProvider
    name: string
    description: string
    cost: string
    available: boolean
  }> {
    return [
      {
        provider: MapProvider.OPENSTREETMAP,
        name: 'OpenStreetMap',
        description: 'Cartes gratuites et open source avec données communautaires',
        cost: 'Gratuit',
        available: true
      },
      {
        provider: MapProvider.GOOGLE,
        name: 'Google Maps',
        description: 'Cartes commerciales avec données complètes et API Places',
        cost: 'Payant (selon usage)',
        available: this.isGoogleMapsAvailable()
      }
    ]
  }
}

// Export d'une instance par défaut pour faciliter l'utilisation
export const mapServiceFactory = MapServiceFactory
